// Ancient ruins render as a tapered obelisk on a wide base. Gold-tinted when
// the treasure is still there; gray granite when already collected.

import { useEffect, useRef } from 'react';
import type * as THREE from 'three';
import {
  type InfraEntry,
  _color,
  _dummy,
  ruinBaseGeo,
  ruinMat,
  ruinShaftGeo,
  ruinTint,
} from './InfrastructureMaterials.js';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt } from './elevation.js';

interface RuinInstancedProps {
  readonly entries: ReadonlyArray<InfraEntry>;
  readonly biomeMap: Map<string, string>;
}

export function RuinInstanced({ entries, biomeMap }: RuinInstancedProps) {
  const baseRef = useRef<THREE.InstancedMesh>(null);
  const shaftRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const base = baseRef.current;
    const shaft = shaftRef.current;
    if (!base || !shaft || entries.length === 0) return;

    const dummy = _dummy;
    const color = _color;
    entries.forEach(({ tile }, idx) => {
      const [x, , z] = tile.transform.position;
      const props = tile.props as Record<string, unknown> | undefined;
      const q = (props?.q as number) ?? 0;
      const r = (props?.r as number) ?? 0;
      const elev = tileElevationAt(q, r, biomeMap);
      const collected = (props?.ruinCollected as boolean | undefined) ?? false;
      const tint = ruinTint(collected);

      // Random yaw per instance for visual variation, hashed from coord so it's stable.
      const yaw = (Math.sin(q * 12.9898 + r * 78.233) * 43758.5453) % (Math.PI * 2);

      // Base — flat plate on the ground.
      dummy.position.set(x, elev + 0.03, z);
      dummy.rotation.set(0, yaw, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      base.setMatrixAt(idx, dummy.matrix);
      color.copy(tint).multiplyScalar(0.55);
      base.setColorAt(idx, color);

      // Shaft — tapered obelisk. Collected ruins are shorter (broken top).
      const shaftScale = collected ? 0.55 : 1.0;
      dummy.position.set(x, elev + 0.06, z);
      dummy.scale.set(1, shaftScale, 1);
      dummy.updateMatrix();
      shaft.setMatrixAt(idx, dummy.matrix);
      color.copy(tint);
      shaft.setColorAt(idx, color);
    });

    base.instanceMatrix.needsUpdate = true;
    shaft.instanceMatrix.needsUpdate = true;
    if (base.instanceColor) base.instanceColor.needsUpdate = true;
    if (shaft.instanceColor) shaft.instanceColor.needsUpdate = true;
    base.count = entries.length;
    shaft.count = entries.length;
  }, [entries, biomeMap]);

  return (
    <>
      <instancedMesh
        ref={baseRef}
        args={[ruinBaseGeo, ruinMat, entries.length]}
        renderOrder={RenderLayer.Infrastructure}
        castShadow
        receiveShadow
      />
      <instancedMesh
        ref={shaftRef}
        args={[ruinShaftGeo, ruinMat, entries.length]}
        renderOrder={RenderLayer.Infrastructure}
        castShadow
        receiveShadow
      />
    </>
  );
}
